import FluentdLogger from 'app/services/fluentdLogger';
import {
  addLocalStorageListener,
  addSessionStorageListener,
  getDangerousNativeLocalStorage,
  getDangerousNativeSessionStorage,
  isStorageClearEvent,
} from 'app/utils/env';
import createStateContext from 'app/utils/createStateContext';
import SubscribableState, { VolatileValue, makeVolatileValue } from 'app/utils/SubscribableState';

export enum StorageType {
  LOCAL,
  SESSION,
}

interface IStorageCache {
  [key: string]: string | null | undefined;
}

// Use module-private symbols so outside code can't access the store's state
// directly.
const storageCache = Symbol('storageCache');
const storageType = Symbol('storageType');
const unsubscribeStorage = Symbol('unsubscribeStorage');

interface IState {
  [storageCache]: IStorageCache;
  [storageType]: StorageType;
  [unsubscribeStorage]: VolatileValue<undefined | (() => void)>;
}

const getStorage = (store: SubscribableState<IState>): Storage | undefined => (
  store.state[storageType] === StorageType.LOCAL ?
    getDangerousNativeLocalStorage() : getDangerousNativeSessionStorage()
);

const addListener = (store: SubscribableState<IState>, callback: (storageEvent: StorageEvent) => void) => (
  store.state[storageType] === StorageType.LOCAL ?
    addLocalStorageListener(callback) : addSessionStorageListener(callback)
);

const updateCacheForKey = (
  store: SubscribableState<IState>,
  key: string,
  { skipNotify }: { skipNotify?: boolean } = {},
) => {
  try {
    const storage = getStorage(store);
    if (storage) {
      const value = storage.getItem(key);

      store.setState({
        [storageCache]: {
          ...store.state[storageCache],
          [key]: value,
        },
      }, { skipNotify });
    }
  } catch (error) {
    FluentdLogger.info({
      name: `StorageError: ${error.name}`,
      message: error.message,
    });
  }
};

export const createStorageStore = (type: StorageType) => {
  const store = new SubscribableState<IState>({
    [storageCache]: {},
    [storageType]: type,
    [unsubscribeStorage]: makeVolatileValue<undefined | (() => void)>(undefined),
  });

  subscribeStorage(store);

  return store;
};

const subscribeStorage = (store: SubscribableState<IState>) => {
  const listener = (storageEvent: StorageEvent) => {
    if (isStorageClearEvent(storageEvent)) {
      store.setState({
        [storageCache]: {},
      });
    } else if (storageEvent.key && store.state[storageCache].hasOwnProperty(storageEvent.key)) {
      // Only update the cache if the key has actually been used in the app
      updateCacheForKey(store, storageEvent.key);
    }
  };

  const unsubscribe = addListener(store, listener);

  store.state[unsubscribeStorage].set(unsubscribe);
};

export const resetStorage = (store: SubscribableState<IState>) => {
  // Undo subscribeStorage()
  store.state[unsubscribeStorage].get()?.();
};

export const getItem = (store: SubscribableState<IState> | undefined, key: string): string | null => {
  if (!store) {
    return null;
  }

  if (!store.state[storageCache].hasOwnProperty(key)) {
    updateCacheForKey(store, key, { skipNotify: true });
  }

  // updateCacheForKey may have updated the state, so re-access `store.state`
  return store.state[storageCache][key] || null;
};

export const setItem = (store: SubscribableState<IState> | undefined, key: string, value: string) => {
  if (!store) {
    throw new Error('Missing <LocalStorageProvider>');
  }

  try {
    const storage = getStorage(store);
    if (storage) {
      storage.setItem(key, value);
    }
  } catch (error) {
    FluentdLogger.info({
      name: `StorageError: ${error.name}`,
      message: error.message,
    });
  }

  store.setState({
    [storageCache]: {
      ...store.state[storageCache],
      [key]: value,
    },
  });
};

export const removeItem = (store: SubscribableState<IState> | undefined, key: string) => {
  if (!store) {
    throw new Error('Missing <LocalStorageProvider>');
  }

  try {
    const storage = getStorage(store);
    if (storage) {
      storage.removeItem(key);
    }
  } catch (error) {
    FluentdLogger.info({
      name: `StorageError: ${error.name}`,
      message: error.message,
    });
  }

  // We keep the key in the cache because sometimes we might remove an item
  // when we can't parse it. However, we don't want to keep trying to fetch it
  // from storage if we know the value is not valid.
  store.setState({
    [storageCache]: {
      ...store.state[storageCache],
      [key]: null,
    },
  });
};

// Session storage
export const {
  Provider: SessionStorageProvider,
  useStore: useSessionStorage,
  useStoreNoSubscribe: useSessionStorageNoSubscribe,
} = createStateContext<IState>();

// Local storage
export const {
  Provider: LocalStorageProvider,
  useStore: useLocalStorage,
  useStoreNoSubscribe: useLocalStorageNoSubscribe,
} = createStateContext<IState>();
